import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type InvoiceStatus = 'Open' | 'AwaitingValidation' | 'Paid' | 'Pending' | 'Cancelled';
export type BillingStatus = 'UpToDate' | 'Open' | 'Overdue';

export interface PixSettings { pixKey: string | null; pixReceiverName: string | null; pixCity: string | null; }
export interface InvoiceProof { id: string; fileName: string; contentType: string; size: number; createdAt: string; }
export interface Invoice {
  id: string;
  tenantId: string;
  tenantName: string;
  reference: string | null;
  description: string;
  amount: number;
  dueDate: string; // yyyy-MM-dd
  status: InvoiceStatus;
  overdue: boolean;
  paidAt: string | null;
  pendingAt: string | null;
  blockAt: string | null;
  adminNote: string | null;
  createdAt: string;
  proofs: InvoiceProof[];
  pixCode: string | null;
}
export interface BillingSummary {
  status: BillingStatus;
  bookingBlocked: boolean;
  blockAt: string | null;
  openCount: number;
  openAmount: number;
  nextDueDate: string | null;
  overdueCount: number;
  pendingCount: number;
  awaitingValidationCount: number;
}
export interface MyBilling { pix: PixSettings; summary: BillingSummary; invoices: Invoice[]; }

export const BILLING_STATUS_LABEL: Record<BillingStatus, string> = {
  UpToDate: 'Em dia',
  Open: 'Em aberto',
  Overdue: 'Em atraso',
};

/** Rótulo da fatura para exibição (vencida em aberto aparece como "Em atraso"). */
export function invoiceLabel(i: Pick<Invoice, 'status' | 'overdue'>): string {
  switch (i.status) {
    case 'Paid': return 'Paga';
    case 'Cancelled': return 'Cancelada';
    case 'Pending': return 'Pendente';
    case 'AwaitingValidation': return 'Em validação';
    default: return i.overdue ? 'Em atraso' : 'Em aberto';
  }
}
export function invoiceSeverity(i: Pick<Invoice, 'status' | 'overdue'>): 'success' | 'secondary' | 'danger' | 'info' | 'warn' {
  switch (i.status) {
    case 'Paid': return 'success';
    case 'Cancelled': return 'secondary';
    case 'Pending': return 'danger';
    case 'AwaitingValidation': return 'info';
    default: return i.overdue ? 'warn' : 'secondary';
  }
}

/** "2026-11-10" → Date local (sem deslocar o dia pelo fuso). */
export function parseDateOnly(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

@Injectable({ providedIn: 'root' })
export class BillingService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/billing`;

  /** Resumo usado pelo alerta do topo; atualizado após enviar comprovante. */
  readonly summary = signal<BillingSummary | null>(null);

  get(): Observable<MyBilling> {
    return this.http.get<MyBilling>(this.base).pipe(tap((b) => this.summary.set(b.summary)));
  }
  loadSummary(): Observable<BillingSummary> {
    return this.http.get<BillingSummary>(`${this.base}/summary`).pipe(tap((s) => this.summary.set(s)));
  }
  uploadProof(invoiceId: string, file: File): Observable<Invoice> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<Invoice>(`${this.base}/invoices/${invoiceId}/proof`, form);
  }
  downloadProof(invoiceId: string, proofId: string): Observable<Blob> {
    return this.http.get(`${this.base}/invoices/${invoiceId}/proofs/${proofId}`, { responseType: 'blob' });
  }
  clear(): void { this.summary.set(null); }
}

/** Abre um arquivo baixado com token (não dá para usar link direto com Authorization). */
export function openBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  // PDF e imagem abrem no navegador; o download fica como alternativa do próprio visualizador
  if (!/^(application\/pdf|image\/)/.test(blob.type)) a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
